import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import { usePermissions } from '../hooks/usePermissions';
import './LocationManagers.css';

interface Location {
  id: number;
  name: string;
  city: string;
}

interface Manager {
  id: number;
  email: string;
  name: string | null;
  is_active: boolean;
  has_logged_in: boolean;
  is_superadmin: boolean;
  is_self: boolean;
  receive_emails: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

interface LocationManagersProps {
  location: Location;
  onProfileChanged?: () => void;
}

const LocationManagers: React.FC<LocationManagersProps> = ({ location, onProfileChanged }) => {
  const { isSuperAdmin } = usePermissions();

  const [managers, setManagers] = useState<Manager[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const [confirmRemove, setConfirmRemove] = useState<Manager | null>(null);

  const fetchManagers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ApiResponse<Manager[]>>(
        `/locations/${location.id}/managers`
      );
      setManagers(response.data.data || []);
    } catch (err) {
      setError(permissionMessage(err) || 'De beheerders van deze locatie konden niet geladen worden.');
    } finally {
      setIsLoading(false);
    }
  }, [location.id]);

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  const filteredManagers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return managers;
    return managers.filter(
      (manager) =>
        manager.email.toLowerCase().includes(query) ||
        (manager.name || '').toLowerCase().includes(query)
    );
  }, [managers, search]);

  const receivingCount = managers.filter((manager) => manager.receive_emails).length;

  const markSaving = (id: number, saving: boolean) =>
    setSavingIds((current) => (saving ? [...current, id] : current.filter((item) => item !== id)));

  const toggleEmails = async (manager: Manager) => {
    markSaving(manager.id, true);
    setError(null);
    setSuccess(null);

    const next = !manager.receive_emails;
    try {
      if (manager.is_self) {
        await apiClient.put(`/me/locations/${location.id}/email-preference`, {
          receive_emails: next,
        });
        onProfileChanged?.();
      } else {
        // Changing someone else's preference is a superadmin action; the
        // backend rejects it for a regular beheerder.
        await apiClient.put(`/locations/${location.id}/managers/${manager.id}/email-preference`, {
          receive_emails: next,
        });
      }

      setManagers((current) =>
        current.map((item) => (item.id === manager.id ? { ...item, receive_emails: next } : item))
      );
      setSuccess(
        next
          ? `${manager.name || manager.email} ontvangt voortaan e-mails van deze locatie.`
          : `${manager.name || manager.email} ontvangt geen e-mails meer van deze locatie.`
      );
    } catch (err) {
      setError(permissionMessage(err) || 'De instelling kon niet opgeslagen worden.');
    } finally {
      markSaving(manager.id, false);
    }
  };

  const addManager = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newEmail.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post<ApiResponse<unknown>>(
        `/locations/${location.id}/managers`,
        { email: newEmail.trim(), name: newName.trim() || undefined }
      );
      setNewEmail('');
      setNewName('');
      setSuccess(response.data.message || 'Beheerder toegevoegd.');
      await fetchManagers();
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De beheerder kon niet toegevoegd worden.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeManager = async () => {
    if (!confirmRemove) return;

    markSaving(confirmRemove.id, true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.delete<ApiResponse<unknown>>(
        `/locations/${location.id}/managers/${confirmRemove.id}`
      );
      setSuccess(response.data.message || 'Beheerder losgekoppeld van deze locatie.');
      setConfirmRemove(null);
      await fetchManagers();
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De beheerder kon niet verwijderd worden.');
    } finally {
      markSaving(confirmRemove.id, false);
    }
  };

  return (
    <div className="location-managers">
      <div className="managers-intro">
        <h2>Beheerders van {location.name}</h2>
        <p>
          Beheerders kunnen de fietsen en reserveringen van deze locatie bijhouden. Met het schuifje
          bepaalt iedereen zelf of er een e-mail komt bij een nieuwe reservering.
        </p>
      </div>

      {error && <div className="managers-message managers-message-error">{error}</div>}
      {success && <div className="managers-message managers-message-success">{success}</div>}

      {isSuperAdmin && (
        <form className="manager-form" onSubmit={addManager}>
          <div className="manager-form-fields">
            <div className="manager-form-group">
              <label htmlFor="manager-email">E-mailadres</label>
              <input
                id="manager-email"
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="naam@ijsselheem.nl"
                required
                disabled={isSaving}
              />
            </div>
            <div className="manager-form-group">
              <label htmlFor="manager-name">Naam (optioneel)</label>
              <input
                id="manager-name"
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Voor- en achternaam"
                disabled={isSaving}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isSaving || !newEmail.trim()}>
              {isSaving ? 'Bezig...' : 'Beheerder toevoegen'}
            </button>
          </div>
          <p className="manager-form-hint">
            Deze persoon krijgt toegang zodra hij of zij voor het eerst inlogt met het
            Microsoft-account van dit e-mailadres.
          </p>
        </form>
      )}

      <div className="managers-toolbar">
        <div className="managers-search">
          <label htmlFor="manager-search" className="visually-hidden">
            Zoek een beheerder
          </label>
          <input
            id="manager-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoek op naam of e-mailadres..."
          />
        </div>
        <div className="managers-count">
          {managers.length} beheerder{managers.length === 1 ? '' : 's'} · {receivingCount} met
          e-mailmelding
        </div>
      </div>

      {isLoading ? (
        <p className="managers-loading">Beheerders laden...</p>
      ) : filteredManagers.length === 0 ? (
        <div className="managers-empty">
          {managers.length === 0
            ? 'Er zijn nog geen beheerders gekoppeld aan deze locatie.'
            : 'Geen beheerder gevonden met deze zoekopdracht.'}
        </div>
      ) : (
        <ul className="managers-list">
          {filteredManagers.map((manager) => {
            const isBusy = savingIds.includes(manager.id);
            const canToggle = manager.is_self || isSuperAdmin;

            return (
              <li key={manager.id} className="manager-row">
                <div className="manager-identity">
                  <span className="manager-name">
                    {manager.name || manager.email}
                    {manager.is_self && <span className="manager-tag">jij</span>}
                    {manager.is_superadmin && <span className="manager-tag tag-super">superadmin</span>}
                    {!manager.has_logged_in && (
                      <span className="manager-tag tag-pending">nog niet ingelogd</span>
                    )}
                  </span>
                  {manager.name && <span className="manager-email">{manager.email}</span>}
                </div>

                <div className="manager-controls">
                  <label className="manager-toggle">
                    <input
                      type="checkbox"
                      checked={manager.receive_emails}
                      onChange={() => toggleEmails(manager)}
                      disabled={!canToggle || isBusy}
                    />
                    <span className="manager-toggle-track" aria-hidden="true">
                      <span className="manager-toggle-thumb" />
                    </span>
                    <span className="manager-toggle-label">
                      {manager.receive_emails ? 'Ontvangt e-mail' : 'Geen e-mail'}
                    </span>
                  </label>

                  {isSuperAdmin && (
                    <button
                      type="button"
                      className="manager-remove"
                      onClick={() => setConfirmRemove(manager)}
                      disabled={isBusy}
                      title={`${manager.name || manager.email} loskoppelen van deze locatie`}
                      aria-label={`${manager.name || manager.email} loskoppelen van deze locatie`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmRemove && (
        <div className="modal-overlay">
          <div className="modal-content managers-confirm">
            <h3>Beheerder loskoppelen</h3>
            <p>
              Weet je zeker dat je <strong>{confirmRemove.name || confirmRemove.email}</strong> wilt
              loskoppelen van {location.name}?
            </p>
            <p className="managers-confirm-hint">
              Deze persoon kan de fietsen en reserveringen van deze locatie daarna niet meer zien.
              Het account zelf blijft bestaan voor eventuele andere locaties.
            </p>
            <div className="managers-confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmRemove(null)}
              >
                Annuleren
              </button>
              <button type="button" className="btn btn-danger" onClick={removeManager}>
                Ja, loskoppelen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagers;
